import { useSetModal } from "@/shared/atoms/useModal";
import useCategoryQueryHook from "@/shared/hooks/useCategoryQueryHook";
import useRecommendedProductPatch from "@/shared/mutations/menu/useRecommendedProductPatch";
import useRecommendedProductQuery from "@/shared/queries/menu/useRecommendedProductQuery";
import { RecommendProduct } from "@/shared/utils/interfaces";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import Button from "../Button/Button";
import Checkbox from "../Checkbox/Checkbox";
import Error from "../Error";
import Modal, { ModalProps } from "../Modal/Modal";
import ModalTitle from "../Modal/ModalTitle";
import Spinner from "../Spinner/Spinner";

interface MenuRecommendedProductModalProps extends ModalProps {}

const MenuRecommendedProductModal = ({
  onExit,
  ...props
}: MenuRecommendedProductModalProps) => {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<
    {
      productId: number;
      recommendedId: number;
    }[]
  >([]);
  const mutation = useRecommendedProductPatch();
  const setModal = useSetModal();
  const [values] = useState({ support: 5, confidence: 10 });
  const { data, isPending, isSuccess, isError, refetch } =
    useRecommendedProductQuery(values.support, values.confidence);
  const query = useCategoryQueryHook();

  const handleSave = () => {
    Promise.all(
      selected.map(({ productId, recommendedId }) =>
        mutation.mutateAsync({
          menuItemID: productId,
          recommendedProductID: recommendedId,
        })
      )
    ).then(() => {
      setModal("", {});
      query.refetch();
    });
  };

  const handleAdd = (product: RecommendProduct, recommendedId: number) => {
    setSelected([
      ...selected,
      { productId: product.product_id, recommendedId: recommendedId },
    ]);
  };
  const handleRemove = (product: RecommendProduct, recommendedId: number) => {
    setSelected(
      selected.filter(
        (s) =>
          s.productId !== product.product_id ||
          s.recommendedId !== recommendedId
      )
    );
  };

  const isSelected = (product: RecommendProduct, recommendedId: number) => {
    const isRecInsideProduct = product.recommended.find(
      (r) => r.product_id === recommendedId
    );
    const isSelected = selected.find(
      (s) => s.productId === product.product_id && Boolean(isRecInsideProduct)
    );
    return Boolean(isSelected);
  };

  return (
    <Modal {...props} onExit={onExit}>
      <ModalTitle>{t("menu.recommendedProductSuggestions")}</ModalTitle>
      <span className="inline-block my-[1rem] font-normal">
        {t("menu.recommendedProductDescription")}
      </span>

      {isPending && <Spinner />}
      {!isPending && isError && <Error refetch={refetch} />}
      {!isPending && isSuccess && (
        <div className="mt-4 flex flex-col gap-8 overflow-y-auto">
          {data?.map((category) => (
            <div key={category.category_id}>
              <div className="bg-primary-dark p-5 cursor-default text-white font-semibold">
                {category.name}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 border-2 border-primary-dark w-full">
                {category?.products?.map((product) => (
                  <div
                    key={product.product_id}
                    className="p-10 border-2 border-border"
                  >
                    <div className="font-semibold">{product.name}</div>
                    {product.recommended.map((rec) => (
                      <div
                        className="mt-8 flex flex-col gap-8"
                        key={rec.product_id}
                      >
                        <Checkbox
                          active={isSelected(product, rec.product_id)}
                          onChange={(e) =>
                            e.target.checked
                              ? handleAdd(product, rec.product_id)
                              : handleRemove(product, rec.product_id)
                          }
                          checked={isSelected(product, rec.product_id)}
                        >
                          {rec.name}
                        </Checkbox>
                        <Button
                          isDisabled={!isSelected(product, rec.product_id)}
                          onClick={handleSave}
                          isPending={mutation.isPending}
                        >
                          {t("save")}
                        </Button>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
};

export default MenuRecommendedProductModal;

/* We will use it in future */
/*  <div className="my-4 flex flex-col gap-8">
        <div className="mr-4 flex gap-4">
          <div flex="1 0 50%">
            <div className="flex justify-between">
              <div as="span">Min support percentage (between 0 - 100)</div>
              <div as="span" mr="0.5rem" fontWeight="600">
                {values.support}
              </div>
            </div>
            <input
              type="range"
              style={{ width: "100%" }}
              min="0"
              max="100"
              value={values.support}
              onChange={(e) =>
                setValues({ ...values, support: Number(e.target.value) })
              }
            />
          </div>
          <div flex="1 0 50%">
            <div className="flex justify-between">
              <div as="span">Min confidence percentage (between 0 - 10)</div>
              <div as="span" mr="0.5rem" fontWeight="600">
                {values.confidence}
              </div>
            </div>
            <input
              type="range"
              style={{ width: "100%" }}
              min="0"
              max="10"
              value={values.confidence}
              onChange={(e) =>
                setValues({ ...values, confidence: Number(e.target.value) })
              }
            />
          </div>
        </div>

        <Button variant="secondary">Find</Button>
      </div> 
*/
